import { useMemo, useState } from "react";
import { ChevronRight, Download } from "lucide-react";
import {
  BUILT_INDEX_STEPS,
  type BuiltIndexStep,
} from "@/lib/built-index-analytics";
import {
  FUNNEL_DIMENSIONS,
  breakdownForStep,
  buildFunnel,
  filterBySegment,
  segmentValues,
  type FunnelDimension,
  type FunnelEvent,
} from "@/lib/funnel-segments";

type Props = {
  events: FunnelEvent[];
  onExport?: (rows: Record<string, unknown>[]) => void;
};

const chip =
  "px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] border border-border transition hover:bg-muted/60";

export function FunnelChart({ events, onExport }: Props) {
  const [dimension, setDimension] = useState<FunnelDimension>("device");
  const [segment, setSegment] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<BuiltIndexStep | null>(null);

  const scoped = useMemo(
    () => filterBySegment(events, dimension, segment),
    [events, dimension, segment],
  );
  const funnel = useMemo(() => buildFunnel(scoped), [scoped]);
  const segments = useMemo(() => segmentValues(events, dimension), [events, dimension]);
  const breakdown = useMemo(
    () => (activeStep ? breakdownForStep(scoped, activeStep, dimension) : []),
    [scoped, activeStep, dimension],
  );

  const top = funnel[0]?.unique || funnel.find((f) => f.unique > 0)?.unique || 0;

  return (
    <section aria-labelledby="funnel-chart-heading">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="funnel-chart-heading" className="font-display uppercase text-xl">
            Interactive funnel
          </h2>
          <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
            Each bar is unique people at that step. Select a bar to drill into who dropped
            off, split by {FUNNEL_DIMENSIONS.find((d) => d.key === dimension)?.label.toLowerCase()}.
          </p>
        </div>
        {onExport && (
          <button
            type="button"
            className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-[0.16em] hover:bg-muted/50 transition"
            onClick={() =>
              onExport(
                funnel.map((f) => ({
                  step: f.step,
                  label: f.label,
                  dimension,
                  segment: segment ?? "(all)",
                  unique_people: f.unique,
                  total_events: f.total,
                  step_conversion_pct: f.stepRate ?? "",
                  drop_off: f.dropOff,
                  drop_off_pct: f.dropOffRate ?? "",
                })),
              )
            }
          >
            <Download className="h-4 w-4" /> Chart CSV
          </button>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          Drill down by
        </span>
        {FUNNEL_DIMENSIONS.map((d) => (
          <button
            key={d.key}
            type="button"
            aria-pressed={dimension === d.key}
            className={`${chip} ${dimension === d.key ? "bg-foreground text-background" : ""}`}
            onClick={() => {
              setDimension(d.key);
              setSegment(null);
            }}
          >
            {d.label}
          </button>
        ))}
      </div>

      {segments.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={segment === null}
            className={`${chip} ${segment === null ? "bg-orange text-background border-orange" : ""}`}
            onClick={() => setSegment(null)}
          >
            All
          </button>
          {segments.slice(0, 12).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={segment === s}
              className={`${chip} ${segment === s ? "bg-orange text-background border-orange" : ""}`}
              onClick={() => setSegment(segment === s ? null : s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <ol className="mt-6 border border-border divide-y divide-border">
        {funnel.map((f) => {
          const width = top === 0 ? 0 : Math.max(f.unique === 0 ? 0 : 2, (f.unique / top) * 100);
          const selected = activeStep === f.step;
          return (
            <li key={f.step}>
              <button
                type="button"
                aria-expanded={selected}
                aria-label={`${f.label}: ${f.unique} people, ${f.dropOff} dropped off`}
                onClick={() => setActiveStep(selected ? null : (f.step as BuiltIndexStep))}
                className={`w-full text-left px-4 py-3 transition hover:bg-muted/40 ${
                  selected ? "bg-muted/50" : ""
                }`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${selected ? "rotate-90" : ""}`}
                      aria-hidden="true"
                    />
                    {f.label}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {f.unique} people · {f.overall}% of top
                    {f.stepRate == null ? "" : ` · ${f.stepRate}% step conv.`}
                    {f.dropOff > 0 ? ` · −${f.dropOff} lost` : ""}
                  </span>
                </div>
                <div className="mt-2 h-3 w-full bg-muted">
                  <div
                    className="h-3 bg-orange transition-[width] duration-500"
                    style={{ width: `${Math.min(100, width)}%` }}
                  />
                </div>
              </button>

              {selected && (
                <div className="px-4 pb-4">
                  {breakdown.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No attribution data for this step yet.
                    </p>
                  ) : (
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full text-sm">
                        <caption className="sr-only">
                          {f.label} drop-off by {dimension}
                        </caption>
                        <thead className="bg-muted/40 text-left">
                          <tr>
                            {[
                              FUNNEL_DIMENSIONS.find((d) => d.key === dimension)?.label ?? "",
                              "Entered",
                              "Reached",
                              "Dropped",
                              "Conv.",
                            ].map((h) => (
                              <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.map((b) => (
                            <tr key={b.segment} className="border-t border-border">
                              <td className="px-3 py-2 whitespace-nowrap">
                                <button
                                  type="button"
                                  className="underline underline-offset-4 hover:text-orange"
                                  onClick={() =>
                                    setSegment(segment === b.segment ? null : b.segment)
                                  }
                                >
                                  {b.segment}
                                </button>
                              </td>
                              <td className="px-3 py-2 tabular-nums">{b.entered}</td>
                              <td className="px-3 py-2 tabular-nums font-semibold">{b.reached}</td>
                              <td className="px-3 py-2 tabular-nums text-muted-foreground">
                                {b.dropOff}
                              </td>
                              <td className="px-3 py-2 tabular-nums">{b.conversion}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {funnel.every((f) => f.unique === 0) && (
        <p className="mt-4 text-sm text-muted-foreground">
          No funnel events recorded in this range yet.
        </p>
      )}
      <p className="sr-only">{BUILT_INDEX_STEPS.length} funnel steps tracked.</p>
    </section>
  );
}

export default FunnelChart;
